import {
    Box,
    Button,
    Chip,
    Collapse,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Tab,
    Tabs,
    Typography,
    Switch,
    Tooltip,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import CopyIcon from '@mui/icons-material/FileCopy';
import React, { MouseEvent, useEffect, useMemo, useState } from 'react';
import { Card as BCard } from 'react-bootstrap';
import { withRouter } from 'react-router-dom/cjs/react-router-dom.min';
import { CustomLabel, Loadable, PopoverLink } from '../../../../common';
import { SummaryTile } from '../../../../common/widgets/SummaryTile';
import { asDate } from '../../../../util/date';
import { fromLocalStorage, toLocalStorage } from '../../../../util/storage';
import { ClaimHistory } from '../../ClaimHistory';
import { ExecutionErrorDialog } from '../../ExecutionErrorDialog';
import { AdvancedTrace } from '../AdvancedTrace';
import { ArchivedClaimDetails } from '../ArchivedClaimDetails';
import { ChangeContractComponent } from '../ChangeContractComponent';
import { ConfirmationDialog } from '../ConfirmationDialog';
import { ChangeStatusComponent } from '../UpdateStatusComponent';
import AssignClaimSection from '../AssignClaim/AssignClaimSection';
import AssignRCMTeamSection from '../AssignRCMTeamSection/AssignRCMTeamSection';
import InfoIcon from '@mui/icons-material/Info';
import AutoModeIcon from '@mui/icons-material/AutoMode';
import ChatIcon from '@mui/icons-material/Chat';
import HistoryIcon from '@mui/icons-material/History';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/reducers';
import { asDollarsWithZero } from '../../../../util/format';
import { useData } from '@pure-ptr/react';
import { getClaimIds, importPatientFhirData } from '../../../../store/api/claimAnalysis';
import { getAllBreadCumbLocations } from '../../../../router/RouterHelpers';
import { ContractInfo } from '../../../../store/api/ai-api';
import SvgIcon from '../../../../svg/SvgIcon';
import MedicalInformationIcon from '@mui/icons-material/MedicalInformation';
import AssignmentReturnedIcon from '@mui/icons-material/AssignmentReturned';
import { checkIfFHIRConfigExists } from '../../../../store/api/clients';
import PatientDetail from '../PatientDetails/PatientDetails';

const useStyles = makeStyles(theme => {
    return {
        titleDiv: {
            padding: '16px 24px',
            borderBottom: `1px solid ${theme.palette.secondary.main}`,
        },
        chip: {
            marginLeft: '15px',
            fontWeight: 600,
            fontSize: '12px'
        },
        underline: {
            textDecoration: 'underline',
            color: '#2739c1',
            cursor: 'pointer'
        },
        mainDiv: {
            padding: '16px 24px',
        },
        sectionHeader: {
            paddingLeft: '28px',
            minHeight: '48px',
            fontSize: '15px',
            display: 'flex',
            alignItems: 'center'
        },
        sectionCard: {
            borderRadius: '8px !important',
            height: '100%'
        },
        sectionHeaderTab: {
            padding: '0 28px',
            backgroundColor: '#EBFFF5'
        },
        box: {
            padding: '0px 20px',
            "& b": {
                '@media (max-width: 1295px)': {
                    fontSize: '12px !important'
                },
                '@media (max-width: 1240px)': {
                    fontSize: '11px !important'
                },
            },
        },
        tabs: {
            '& button': {
                minWidth: 'unset !important',
                padding: '0 5px',
                textTransform: 'none !important',
                fontSize: '14px'
            },
            '& .MuiTabs-indicator': {
                minWidth: 'unset !important'
            }
        },
        tabBody: {
            padding: '0 30px'
        },
        tabSecBorder: {
            border: '0.56px solid  rgba(0, 0, 0, 0.25)',
            marginLeft: '-2px'
        },
        tabSecLeft: {
            paddingRight: '30px !important'
        },
        tabSecRight: {
            paddingLeft: '30px !important'
        },
        basicDetailsGrid: {
            '& .loadable': {
                marginBottom: '0 !important'
            }
        },
        prevNextButtons: {
            color: '#72777f',
            textTransform: 'none',
            '& .MuiButton-startIcon': {
                marginRight: '0px !important'
            },
            '& .MuiButton-endIcon': {
                marginLeft: '0px !important'
            }
        },
        dataViewButton: {
            marginTop: '6px'
        },
        moreChip: {
            color: '#94c83d',
            '& a': {
                color: '#94c83d',
                textDecoration: 'none',
            },
        },
        totalExpectedPayLabel: {
            marginLeft: '0.5rem'
        },
        eapgGroupsSummary: {
            maxHeight: '260px',
            overflowY: 'auto',
        },
        eapgIssuesSummary: {
            maxHeight: '160px',
            overflowY: 'auto',
            paddingRight: '8px'
        },
        stepContainer: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            position: 'relative',
            padding: '40px 20px',
        },
        circle: {
            width: 40,
            height: 40,
            borderRadius: '50%',
            backgroundColor: '#888',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 'bold',
            fontSize: 16,
            cursor: 'pointer',
        },
        date: {
            marginTop: 8,
            fontWeight: 600,
        },
        line: {
            position: 'absolute',
            top: '50%',
            left: '100%',
            transform: 'translate(-25%, -50%)',
            width: 60,
            height: 2,
            backgroundColor: '#ccc',
        },
        tooltipPaper: {
            padding: 12,
            minWidth: 800,
            maxWidth: 800,
            overflowX: 'auto',
        },
        tableHeadCell: {
            fontWeight: 'bold',
            whiteSpace: 'nowrap',
        },
        tableCell: {
            whiteSpace: 'nowrap',
        },
    }
});

function BasicDetailsSection(props) {
    const shouldUseClaimBackup = (function () {
    try {
        const histories = getAllBreadCumbLocations();

        if(histories?.length==0){
            return false;
        }

        const summaryDashboardIndex = histories.map(path => path?.includes('cms/summary-dashboard')).lastIndexOf(true);
        const claimAnalysisIndex = histories.map(path => path?.includes('/cms/claimAnalysis')).lastIndexOf(true);
        
        // Return true only if claimAnalysis exists AND is more recent than summaryDashboard
        // (or summaryDashboard doesn't exist)
        return claimAnalysisIndex !== -1 && 
               (summaryDashboardIndex === -1 || claimAnalysisIndex > summaryDashboardIndex);
        
    } catch (e) {
        console.error('Error processing history:', e);
        return false;
    }
})();

    const ContractsApi = new ContractInfo()
    const { isFromModel, isFromTriage, isFromClaimAnalysis, claimAllDetails,canAccessPayerMapping, hasJSONViewAccess,
         recoveredAmountFlag, sections = [], chargeLevelSections = [], claimHistory = [], reprocessClaimCallback,
        drg, drgDescription, principalDx, principalDxDescription, getClaimDetailsPending, claimBulkOnDemandPending, 
        claimId, comment, consolidatedClaims,assignClaimsCallback, principalPx, principalPxDescription, principalPxDate,
        openChat, closeChat,
        actions: { setAdjustment, setClaimServiceAdjustments, processBulkOnDemand, 
            setClaimCommentsConfirmationDialog, getConsolidatedClaims, 
            getModelConsolidatedClaims, resetConsolidatedClaimsView, setMulti,
            resetModelConsolidatedClaimsView, getClaimStatuses } } = props;

    const [isPatientDetailsOpen, setPatientDetailsOpen] = useState(false);
    const [isTraceOpen, setTraceOpen] = useState(false);
    const [isFHIRConfigured, setIsFHIRConfigured] = useState(false);
    const [jsonViewOpen, setJsonViewOpen] = useState(false);
    const [isHistoryOpen, setHistoryOpen] = useState(false);
    const [isOpenAppealpage, setAppealPage] = useState(false);
    const [isProcessClaimResponseDialogOpen, setClaimProcessResponseDialog] = useState(false);
    const [processClaimResponse, setProcessClaimResponse] = useState(null);
    const [tab, setTab] = React.useState(0);
    const [isClaimnumberCopied, setCopied] = React.useState(false);
    const [isUHCPayer, setUHCpayer] = useState(true);

    const hasClaimChatBotAccess = useSelector<RootState>( state => state?.clients.hasClaimChatBotAccess);
  
    const [claimAnalysisBackup, setClaimAnalysisBackup] = useState((() => {    
    let result;
    let source;
    
    if (isFromModel) {
        result = fromLocalStorage('modelAnalysis-backup');
        source = 'modelAnalysis-backup';
    } else if (isFromTriage) {
        result = fromLocalStorage('triage-backup');
        source = 'triage-backup';
    } else if (isFromClaimAnalysis && shouldUseClaimBackup) {
        result = fromLocalStorage('claimAnalysis-backup');
        source = 'claimAnalysis-backup';
    } else {
        result = [];
        source = 'default empty array';
    }
    
    return result;
})());

    const isDevOrStage = window.location.host === "dev.slicedhealth.com" || 
                         window.location.host === "staging.slicedhealth.com" ||
                         window.location.host.includes("localhost");

    const classes = useStyles();

    useEffect(() => {
        if (claimId) {
            isFromModel ? getModelConsolidatedClaims(claimId.replace('/', ',')) : getConsolidatedClaims(claimId)
            return () => {
                resetConsolidatedClaimsView();
                resetModelConsolidatedClaimsView();
            }
        }
    }, [claimId, isFromModel]);

    useEffect(() => {
        getClaimStatuses();
    }, [getClaimStatuses]);

    const handleChange = (event, newValue) => {
        setTab(newValue);
    };

    const handleViewClaimAdjustment = () => {
        setAdjustment({
            open: true,
            type: 'CLAIM',
            id: claimAllDetails?.paymentid
        });
    }
    function handleViewClaimServiceAdjustment() {
        setClaimServiceAdjustments({
            open: true,
            id: claimAllDetails?.paymentid
        });
    }

    const handleOpenJsonView = () => {
        if (isFromModel) {
            localStorage.setItem('model_detail_Ids', claimId);
            props.history.push(`/cms/modeling/data-view/${claimAllDetails?.claimnumber}`);
        }
        else {
            props.history.push(isFromTriage ? `/cms/confidence-score-triage/data-view/${claimAllDetails?.claimnumber}/${claimId}` : `/cms/claimAnalysis/data-view/${claimAllDetails?.claimnumber}/${claimId}`);
        }
    }
    const handleCloseJsonView = () => {
        setJsonViewOpen(false);
    }

    function handleOpenHistory(event) {
        setHistoryOpen(true);
    }

    function handleCloseHistory() {
        setHistoryOpen(false);
    }
    const handleClaimAudits = (params) => {
        const claimNumberStr = document.getElementById('claimnumber-chip').textContent;
        const claimNumber = claimNumberStr.substring(1);

        setMulti({filter:[]})
        props.history.push(isFromTriage ? `/admin/claimAudits/${claimNumber}` : `/cms/claimAudits/${claimNumber}`);
    }

    const handleCopyClaimNumber = () => {
        const claimNumberStr = document.getElementById('claimnumber-chip').textContent;
        const claimNumber = claimNumberStr.substring(1);
        navigator.clipboard.writeText(claimNumber);
        setCopied(true);
        setTimeout(() => {
            setCopied(false);
        }, 3000);
    }

    const handleReprocessClaim = () => {
        const payload = {
            claimMetadata: [
                {
                    claimNumber: claimAllDetails?.claimnumber,
                    contractId: claimAllDetails?.contractid,
                }
            ]
        };
        processBulkOnDemand(payload, reprocessCallback);
    }

    const handleClaimAppeal = () =>{
        const claimNumberStr = document.getElementById('claimnumber-chip').textContent;
        const claimNumber = claimNumberStr.substring(1);   
        props.history.push({
            pathname: `/cms/claimAnalysis/claimAppeal/${claimNumber}/${claimId}`,
        });        
    }
    
    const handleNextClaim = (e: MouseEvent, confirm?: any) => {
        // Close chat sidebar when navigating
        if (closeChat) {
            closeChat();
        }
        
        if (comment && !confirm) {
            setClaimCommentsConfirmationDialog({ open: true, state: 'next' });
            return
        }

        const currentIndex = claimAnalysisBackup.findIndex(item => item === (isFromModel ? claimId : parseInt(claimId)));

        const nextindex = currentIndex + 1;
        const nextClaimId = claimAnalysisBackup[nextindex];
        if (nextClaimId) {
            props.history.push(isFromModel ? `/cms/modeling/results/view/${nextClaimId}`: isFromTriage ? `/cms/confidence-score-triage/view/${nextClaimId}` : `/cms/claimAnalysis/view/${nextClaimId}`)
        }
    }

    const handlePreviousClaim = (e: MouseEvent, confirm?: any) => {
        // Close chat sidebar when navigating
        if (closeChat) {
            closeChat();
        }
        
        if (comment && !confirm) {
            setClaimCommentsConfirmationDialog({ open: true, state: 'previous' });
            return
        }

        const currentIndex = claimAnalysisBackup.findIndex(item => item === (isFromModel ? claimId : parseInt(claimId)));

        const prevIndex = currentIndex - 1;
        const prevClaimId = claimAnalysisBackup[prevIndex];
        if (prevClaimId) {
            props.history.push(isFromModel ? `/cms/modeling/results/view/${prevClaimId}` : isFromTriage ? `/cms/confidence-score-triage/view/${prevClaimId}` : `/cms/claimAnalysis/view/${prevClaimId}`)
        }
    }

    const { isPreviousButtonAvailable, isNextButtonAvailable } = useMemo(() => {
        const currentIndex = claimAnalysisBackup?.findIndex(
        (item) => item === (isFromModel ? claimId : parseInt(claimId))
        );

        return {
        isPreviousButtonAvailable: currentIndex > 0,
        isNextButtonAvailable:
            currentIndex !== (claimAnalysisBackup?.length - 1) && currentIndex > -1,
        };
    }, [claimId, isFromModel, claimAnalysisBackup]);


    const [claimAnalysisFilterPage] = useState(fromLocalStorage('claimAnalysis-filter-page') ?? null)
    const [claimAnalysisPageSession, setClaimAnalysisPageSession] = useState(JSON.parse(sessionStorage.getItem('page-state:claimAnalysis-landing'))?.pagination ?? null)

    const [getNextPageClaimsIds, setGetNextPageClaimsIds] = useState(null);
    const { data: claimIds, isPending: isFetchingClaimIds } = useData(
        async abort => {
            if (!getNextPageClaimsIds) return;
            
            
            const options = {...claimAnalysisFilterPage, page: {
                ...claimAnalysisFilterPage.pagination, currentPage: getNextPageClaimsIds.page
            }}
            delete options.significance;
            delete options.pagination;
            return await getClaimIds(claimAnalysisFilterPage?.significance || false, options);
        },
        [getNextPageClaimsIds]
    );

    const {} = useData(async () => {
            try{
                const response = await checkIfFHIRConfigExists();
                setIsFHIRConfigured(response);
                return response;
            }
            catch(error){
            console.error(error);
            }
            
        }, []);


    useEffect(() => {
        if(!claimAnalysisFilterPage) return;
        // response: {results: [{id: 1}, {id: 2}, ...], page: {current: 1, total: 10, size: 25}}
        if (claimIds) {
            const newClaimIds = claimIds.results.map(item => item.id);
            if (!!getNextPageClaimsIds) {
                // append new ids to existing backup
                const existingIds = isFromModel ? fromLocalStorage('modelAnalysis-backup') : isFromTriage ? fromLocalStorage('triage-backup') : isFromClaimAnalysis && shouldUseClaimBackup ? fromLocalStorage('claimAnalysis-backup') : [];
                let updatedIds ;
                if (getNextPageClaimsIds.mode === 'next') {
                    updatedIds = [...existingIds, ...newClaimIds];
                } else if (getNextPageClaimsIds.mode === 'previous') {
                    updatedIds = [...newClaimIds, ...existingIds];
                }
                const uniqueIds = [...new Set(updatedIds)]; // remove duplicates
                setClaimAnalysisBackup(uniqueIds);
                if (isFromModel) {
                    localStorage.setItem('modelAnalysis-backup', JSON.stringify(uniqueIds));
                } else if (isFromTriage) {
                    localStorage.setItem('triage-backup', JSON.stringify(uniqueIds));
                } else if (isFromClaimAnalysis && shouldUseClaimBackup) {
                    localStorage.setItem('claimAnalysis-backup', JSON.stringify(uniqueIds));
                }


                let finalData = uniqueIds.map(id => ({
                    id,
                    page: getNextPageClaimsIds.page
                }));
                toLocalStorage('claimAnalysis-backup-page', [
                    ...fromLocalStorage('claimAnalysis-backup-page')?.filter(item => item.page !== getNextPageClaimsIds.page) || [],
                    ...finalData
                ]);
            } else {
                setClaimAnalysisBackup(newClaimIds);
            }
        }   
    }, [claimIds, isFetchingClaimIds, claimAnalysisFilterPage]);


      useEffect(() => {
            if(claimAnalysisFilterPage){
                
                if (!isPreviousButtonAvailable) {
                    // Check filter is available in local storage
                    if (!!claimAnalysisPageSession && claimAnalysisPageSession?.currentPage > 1) {
                        setGetNextPageClaimsIds({
                            id: getNextPageClaimsIds?.id || 1 + 1,
                            page: claimAnalysisPageSession.currentPage - 1 === 0 ? 1 : claimAnalysisPageSession?.currentPage - 1,
                            mode: 'previous'
                        });
                        
                    }
                }
                if (!isNextButtonAvailable) {
                    // Check filter is available in local storage
                    if (!!claimAnalysisPageSession && claimAnalysisPageSession?.currentPage < claimAnalysisPageSession?.totalPages) {
                        setGetNextPageClaimsIds({
                            id: getNextPageClaimsIds?.id || 1 + 1,
                            page: claimAnalysisPageSession?.currentPage + 1,
                            mode: 'next'
                        });

                        
                    }
                }
            }

            return () => {
                setGetNextPageClaimsIds(null);
                setClaimAnalysisPageSession(null);
            }
        }, [isPreviousButtonAvailable, isNextButtonAvailable]);


    useEffect(() => {
        let stateSessionStorage = JSON.parse(sessionStorage.getItem('page-state:claimAnalysis-landing')) ?? null;

        let listOfDetails = JSON.parse(localStorage.getItem('claimAnalysis-backup-page')) ?? [];

        // get current detail id from state session storage
        const currentDetailId = claimAllDetails?.claimnumber || null;

        if( currentDetailId && listOfDetails.length > 0 ) {
            let currentDetail = listOfDetails.find(item => item.id === (isFromModel ? claimId : parseInt(claimId)));

            // update page state in session storage
            stateSessionStorage = {
                ...stateSessionStorage,
                pagination:{
                    ...stateSessionStorage?.pagination,
                    currentPage: currentDetail?.page || stateSessionStorage?.pagination?.currentPage,
                }
            }

            // Add null check for stateSessionStorage as it is throwing error in some browsers

            if(Object(stateSessionStorage).hasOwnProperty('filters')) {
                sessionStorage.setItem('page-state:claimAnalysis-landing', JSON.stringify(stateSessionStorage));
            }
            
            setClaimAnalysisPageSession(stateSessionStorage?.pagination ?? null);
        }
    }, [getNextPageClaimsIds, claimAllDetails]);

    function reprocessCallback(resArr) {

        const processItem = resArr.find(item => item.processingState === 'IGNORED' || item.processingState === 'FAILED');

        // display of error when processing state is ignored or failed.
        if (processItem) {
            setProcessClaimResponse(processItem);
            setClaimProcessResponseDialog(true);
        } else {
            reprocessClaimCallback();
        }
    }

    function handleProcessClaimDialogClose() {
        setClaimProcessResponseDialog(false);
        setProcessClaimResponse(null);

        // if status is ignored redirecting to claim analysis
        if (processClaimResponse?.processingState === 'IGNORED') {
            props.history.push(`/cms/claimAnalysis`);
        }
        else {
            // getting claim details after viewing error details
            reprocessClaimCallback();
        }
    }

    function renderPatientName() {
        return claimAllDetails?.patientLastname ? claimAllDetails?.patientLastname + ', ' + `${claimAllDetails?.patientFirstname ?? ''} ${claimAllDetails?.patientMiddlename ?? ''}` : ''
    }

    function renderPhysicianName() {
        return claimAllDetails?.physicianLastname ? claimAllDetails?.physicianLastname + ', ' + `${claimAllDetails?.physicianFirstname ?? ''}` : ''
    }

    function a11yProps(index) {
        return {
            id: `vertical-tab-${index}`,
            'aria-controls': `vertical-tabpanel-${index}`,
        };
    }

    const handleDialogClose = () => {
        setClaimCommentsConfirmationDialog({ open: false, state: '' });
    }

    const getStatusText = (code) => {
    const normalized = code == null ? "" : String(code);

    switch (normalized) {
        case "1":
        case "19":
        return "Primary";
        case "22":
        return "Reversal";
        case "4":
        return "Denial";
        default:
        return normalized || "Unknown";
    }
    };

    const getRemarks = (item) => {
        return [
            item.remarkCode03,
            item.remarkCode04,
            item.remarkCode05,
            item.remarkCode06,
            item.remarkCode07,
        ].filter(Boolean).join(', ');
    };
    const [claimAnalysisId, setClaimAnalysisId] = useState(null);

    useEffect(() => {
        if (claimAllDetails?.id) {
            setClaimAnalysisId(claimAllDetails?.id);
        }

        return () => {
            setClaimAnalysisId(null);
        };
    }, [claimAllDetails?.id]);

    const getCasAdjustments = (item) => {
        return item?.casCodes?.segment?.map(seg => `${seg.groupcode}${seg.reasoncode} - ${asDollarsWithZero(seg.amount)}`).filter(Boolean).join(', ');
    };
       const { data:chatHistoryData } = useData(async abort => {
           if(!claimAnalysisId) return
           if(!hasClaimChatBotAccess) return
           return await ContractsApi.getAllChatSessions(claimAnalysisId,'claim')
       }, [claimAnalysisId,hasClaimChatBotAccess]);


    return (
        (<Grid className={classes.basicDetailsGrid}>
            <Loadable loading={getClaimDetailsPending || claimBulkOnDemandPending}>
                <Grid container className={classes.titleDiv}>
                    <Grid xs={8} md={7} lg={8}>
                        <Grid container alignItems='center'>
                            <Grid>
                                {isPreviousButtonAvailable &&
                                    <Button onClick={handlePreviousClaim} className={classes.prevNextButtons} startIcon={<ArrowBackIcon />} disabled={getClaimDetailsPending || claimBulkOnDemandPending}>
                                        Previous
                                    </Button>
                                }
                                {isNextButtonAvailable && <Button onClick={handleNextClaim} className={classes.prevNextButtons} endIcon={<ArrowForwardIcon />} disabled={getClaimDetailsPending || claimBulkOnDemandPending}>Next</Button>}
                            </Grid>
                            <Chip id='claimnumber-chip' className={classes.chip} label={`#${claimAllDetails?.claimnumber ?? ''}`} color="primary" variant="outlined" />
                            <IconButton
                                disabled={!claimAllDetails?.claimnumber}
                                onClick={handleCopyClaimNumber}
                                color={isClaimnumberCopied ? 'secondary' : 'primary'}
                                size="large">
                                <CopyIcon />
                                {isClaimnumberCopied && <span className='font-12 ml-2'>Copied</span>}
                            </IconButton>

                            {
                                (!isFromModel  && hasClaimChatBotAccess) && <Box display="flex">
                                    <Tooltip title="SlicedIQ Insights">
                                        <IconButton
                                            size="small"
                                            onClick={
                                            ()=> {
                                                openChat({
                                                    contractId: claimAllDetails?.id,
                                                    sessionId: 0,
                                                    directoryId: 'root',
                                                    claimNumber: claimAllDetails?.claimnumber
                                                });
                                            }
                                            }
                                            disabled={claimAllDetails?.hasExecutionError || !claimAllDetails?.id}
                                            style={{
                                                filter: claimAllDetails?.hasExecutionError || !claimAllDetails?.id ? 'grayscale(1)' : 'none',
                                            }}
                                        >
                                            
                                                    <SvgIcon  className='chat-icon' id='chat-icon' width="19" height="19" />
                                            
                                        </IconButton>
                                    </Tooltip>
                                    {
                                        chatHistoryData && chatHistoryData.length > 0 && (<IconButton
                                    size="small"
                                    disabled={claimAllDetails?.hasExecutionError  || !claimAllDetails?.id}
                                    onClick={() =>
                                        props.history.push(
                                            "/cms/claimAnalysis/ai-chats-history/" +
                                                claimAllDetails?.id + "?claimNumber=" + claimAllDetails?.claimnumber
                                        )
                                    }
                                    
                                >
                                    <Tooltip title="History">
                                        <HistoryIcon />
                                    </Tooltip>
                                </IconButton>)
                                    }
                                
                            </Box>
                            }
                            {isFHIRConfigured && 
                                <IconButton
                                    size='small'
                                    // color='primary'
                                    title={'Patient Details'}
                                    onClick={() => setPatientDetailsOpen(true)}
                                >
                                    <MedicalInformationIcon />
                                </IconButton>
                            }
                            {isDevOrStage && claimAllDetails?.isDenial &&(
                                <IconButton
                                    size="small"
                                    title="Reconsideration/Appeal"
                                    onClick={handleClaimAppeal}
                                >
                                    <AssignmentReturnedIcon />
                                </IconButton>
                            )}                      
                                                        
                            {consolidatedClaims && consolidatedClaims.length > 0 &&
                                consolidatedClaims?.map((cClaim, index) => {
                                    if (index <= 3) {
                                        return <Chip id={`cc-chip-${index}`} className={classes.chip} label={`#${cClaim ?? ''}`} color="secondary" variant="outlined" />
                                    }
                                    else if (index === 4) {
                                        let remainingClaimsIds = consolidatedClaims?.slice(index, consolidatedClaims?.length);
                                        let remainingClaims = (consolidatedClaims?.length - index);
                                        return <Chip id={`cc-chip-${index}`} className={`${classes.chip} ${classes.moreChip}`} label={
                                            <PopoverLink
                                                columnData={`+${remainingClaims < 0 ? 0 : remainingClaims} more`}
                                                hoverData={[
                                                    {
                                                        label: "",
                                                        data: <ul>{remainingClaimsIds.map((cc, i) => { return <li key={i}>  {cc} </li> })}</ul>
                                                    }
                                                ]}
                                                title={`Consolidated Claims`}
                                            />
                                        } color="secondary" variant="outlined" />
                                    }
                                })
                            }
                        </Grid>
                        {!isFromModel && <Grid container>
                            <Typography onClick={handleViewClaimAdjustment} color='primary' className={`${classes.underline} mr-2`} >
                                Claim Adjustments
                            </Typography>
                            <Typography onClick={handleViewClaimServiceAdjustment} color='primary' className={`${classes.underline} mr-2`} >
                                Service Adjustments
                            </Typography>
                            {hasJSONViewAccess &&
                                <Typography onClick={handleOpenJsonView} color='primary' className={`${classes.underline} mr-2`} >
                                    Data View
                                </Typography>
                            }
                            {sections && sections.length > 0 && <Typography onClick={() => setTraceOpen(true)} color='primary' className={`${classes.underline} mr-2`} >
                                Trace
                            </Typography>}
                            <Typography onClick={handleOpenHistory} color='primary' className={`${classes.underline}`} >
                                History
                            </Typography>

                            <Typography onClick={handleClaimAudits} color='primary' className={`${classes.underline} ml-2`} >
                                Claim Audit
                            </Typography>
                        </Grid>}

                    </Grid>
                    {!isFromModel && <Grid xs={4} md={5} lg={4}>
                        <Grid>
                            <CustomLabel label="Payer Name" data={claimAllDetails?.usePlanIdMapping ? claimAllDetails?.planPayerContract?.payerName:claimAllDetails?.payer?.name}
                            />
                        </Grid>
                        <Grid>
                            <ChangeContractComponent claimAnalysis={claimAllDetails} isContractOverriden={claimAllDetails?.overridenContractid} setShouldReloadGrid={false} handleReprocessClaim={handleReprocessClaim} reprocessCallback={reprocessCallback} />

                            {claimAllDetails?.overridenCriteriaContractId && 
                                <Tooltip title="System Overridden">
                                    <AutoModeIcon style={{ marginLeft: '5px', fontSize: '22px' }} />
                                </Tooltip>
                            }
                            {claimAllDetails?.hasExecutionError && <ExecutionErrorDialog executionError={claimAllDetails?.executionError} />}

                        </Grid>
                    </Grid>}
                    {isFromModel &&
                        <Grid xs={4} md={5} lg={4} style={{ textAlign: 'right' }}>
                            <Button className='dataViewButton' onClick={handleOpenJsonView} color='primary' variant='contained'>
                                Data View
                            </Button>
                        </Grid>
                    }

                </Grid>
                <Grid className={classes.mainDiv}>
                    <Grid container spacing={3}>
                        <Grid sm={7} md={7} lg={7} xl={8} item>
                            <BCard className={classes.sectionCard}>
                                <BCard.Header as="h5" className={`${classes.sectionHeaderTab}`}>
                                    <Tabs
                                        value={tab}
                                        onChange={handleChange}
                                        aria-label="Claim details Tab"
                                        className={classes.tabs}
                                    >
                                        <Tab label="Claim Info" {...a11yProps(0)} />
                                        {!isFromModel && <Tab className='pl-3' label="Demographics" {...a11yProps(1)} />}
                                        {!isFromModel && <Tab className='pl-3' label="Remit Timeline" {...a11yProps(2)} />}
                                        {!isFromModel && <Tab className='pl-3' label="Version" {...a11yProps(3)} />}
                                        {!isFromModel && <Tab className='pl-3' label="Follow-Up Call" {...a11yProps(4)} />}
                                        {!isFromModel && (claimAllDetails?.eapgClaimExecutionGroups?.length > 0 || claimAllDetails?.eapgClaimExecutionIssues?.length > 0) && <Tab className='pl-3' 
                                        label={
                                            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                EAPG
                                                <PopoverLink
                                                    columnData={<InfoIcon fontSize='small' color="action" />}
                                                    hoverData={[
                                                        {
                                                            label: "",
                                                            data: "Reports are produced, in part, by using Solventum’s proprietary computer software created, licensed, and/or owned by Solventum Health Information Systems, Inc. All copyrights in and to the Software licensed by Solventum Health Information Systems, Inc. hereunder are owned by Solventum or its affiliates.  All rights reserved."
                                                        }
                                                    ]}
                                                    title={""}
                                                    maxWidth={800}
                                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                                />
                                            </span>
                                        } 
                                        {...a11yProps(5)} />}
                                        {!isFromModel && claimAllDetails?.eapgClaimExecutionSchedules?.length > 0 && <Tab className='pl-3' label="EAPG Schedule Summary" {...a11yProps(6)} />}
                                        
                                    </Tabs>
                                </BCard.Header>
                                <BCard.Body className='px-0'>
                                    <Collapse in={true} timeout="auto" unmountOnExit>
                                        <Box marginTop={1} marginBottom={1}>
                                            <Grid className={classes.tabBody}>
                                                {tab === 0 ? <>
                                                    {isFromModel &&
                                                        <Grid container spacing={2}>
                                                            <Grid item xs={6} className={classes.tabSecLeft}>
                                                                <Grid className='d-flex justify-content-between'>
                                                                    <CustomLabel label="Visit" data={claimAllDetails?.visit} />
                                                                </Grid>
                                                                <Grid className='d-flex justify-content-between'>
                                                                    <CustomLabel label="DRG" data={drg} />
                                                                </Grid>

                                                                <hr />
                                                                <Grid className='d-flex justify-content-between'>
                                                                    <CustomLabel label="Principal DX" data={principalDx} />
                                                                </Grid>
                                                            </Grid>
                                                            <Grid className={classes.tabSecBorder}></Grid>
                                                            <Grid item xs={6} className={classes.tabSecRight}>
                                                                <Grid className='d-flex justify-content-between'>
                                                                    <CustomLabel label="Service Date" data={asDate(claimAllDetails?.servicedate) || 'N/A'} />
                                                                </Grid>
                                                                <hr />

                                                                <Grid className='d-flex justify-content-between'>
                                                                    <CustomLabel label="Updated At" data={asDate( claimAllDetails?.updatedAt, 'date-time') || 'N/A'} />
                                                                </Grid>
                                                                <Grid className='d-flex justify-content-between'>
                                                                    <CustomLabel label="Updated By" data={claimAllDetails?.updatedBy} />
                                                                </Grid>
                                                            </Grid>
                                                        </Grid>
                                                    }
                                                    {!isFromModel &&
                                                        <Grid container spacing={2}>
                                                            <Grid item xs={6} className={classes.tabSecLeft}>
                                                                <Grid className='d-flex justify-content-between'>
                                                                    <CustomLabel label="Visit" data={claimAllDetails?.visit} />
                                                                </Grid>
                                                                <Grid className='d-flex justify-content-between'>
                                                                    <CustomLabel label="Denial" data={claimAllDetails?.isDenial ? <span className='text-danger'><b>Yes</b></span> : 'No'} />
                                                                </Grid>
                                                                <hr />
                                                                <Grid className='d-flex justify-content-between'>
                                                                    <CustomLabel label="Is Paid" data={claimAllDetails?.isPaid ? 'Yes' : 'No'} />
                                                                </Grid>
                                                                
                                                                <Grid className='d-flex justify-content-between'>
                                                                    <CustomLabel label="DRG"
                                                                        data={
                                                                            <PopoverLink
                                                                                columnData={drg}
                                                                                hoverData={[
                                                                                    {
                                                                                        label: "",
                                                                                        data: drgDescription
                                                                                    }
                                                                                ]}
                                                                                title={drg}
                                                                            />
                                                                        }
                                                                    />
                                                                </Grid>
                                                                {principalPx && <Grid className='d-flex justify-content-between'>
                                                                    <CustomLabel label="Principal PX"
                                                                        data={
                                                                            <PopoverLink
                                                                                columnData={principalPx}
                                                                                hoverData={[
                                                                                    {
                                                                                        label: "",
                                                                                        data: principalPxDescription
                                                                                    }
                                                                                ]}
                                                                                title={principalPx}
                                                                            />
                                                                        }
                                                                    />
                                                                </Grid>
                                                                }
                                                                <hr />
                                                                <Grid className='d-flex justify-content-between'>
                                                                    <CustomLabel label="Principal DX"
                                                                        data={
                                                                            <PopoverLink
                                                                                columnData={principalDx}
                                                                                hoverData={[
                                                                                    {
                                                                                        label: "",
                                                                                        data: principalDxDescription
                                                                                    }
                                                                                ]}
                                                                                title={principalDx}
                                                                            />
                                                                        }
                                                                    />
                                                                </Grid>
                                                                <hr />

                                                                <AssignClaimSection claimAllDetails={claimAllDetails} assignClaimsCallback={assignClaimsCallback}/>
                                                                {canAccessPayerMapping &&
                                                                    <Grid className='d-flex justify-content-between' sx={{ mt: "20px" }}>
                                                                        <CustomLabel
                                                                            label="Processed Through Payer Mapping"
                                                                            data={
                                                                                <Typography>{claimAllDetails?.usePlanIdMapping ? "Yes" : "No"}</Typography>

                                                                            }
                                                                            type="claim-contract"
                                                                        />
                                                                    </Grid>}
                                                                        
                                                            </Grid>
                                                            <Grid className={classes.tabSecBorder}></Grid>
                                                            <Grid item xs={6} className={classes.tabSecRight}>
                                                                <Grid className='d-flex justify-content-between'>
                                                                    {claimAllDetails?.visit === "INPATIENT" ?
                                                                        <CustomLabel label="Service Date" data={`${asDate(claimAllDetails?.fromDate) || 'N/A'} - ${asDate(claimAllDetails?.throughDate) || 'N/A'}`} />
                                                                        : <CustomLabel label="Service Date" data={asDate(claimAllDetails?.servicedate) || 'N/A'} />
                                                                    }
                                                                </Grid>
                                                                <Grid className='d-flex justify-content-between'>
                                                                    <CustomLabel label="Remit Date" data={asDate(claimAllDetails?.remitDate) || 'N/A'} />
                                                                </Grid>
                                                                <Grid className='d-flex justify-content-between'>
                                                                    <CustomLabel label="Acknowledged Date" data={asDate(claimAllDetails?.acknowledgementDate) || 'N/A'} />
                                                                </Grid>
                                                                <hr />

                                                                <Grid className='d-flex justify-content-between'>
                                                                    <CustomLabel label="Updated At" data={asDate( claimAllDetails?.updatedAt, 'date-time') || 'N/A'} />
                                                                </Grid>
                                                                <Grid className='d-flex justify-content-between'>
                                                                    <CustomLabel label="Updated By" data={claimAllDetails?.updatedBy} />
                                                                </Grid>

                                                                <ChangeStatusComponent claimAnalysis={claimAllDetails} setShouldReloadGrid={false} handleReprocessClaim={handleReprocessClaim} reprocessCallback={reprocessCallback} />
                                                                <AssignRCMTeamSection claimAllDetails={claimAllDetails} assignClaimsCallback={assignClaimsCallback}/>
                                                            </Grid>
                                                        </Grid>
                                                    }
                                                </> : tab === 1 ? <>
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={6} className={classes.tabSecLeft}>
                                                            <Grid className='d-flex justify-content-between'>
                                                                <CustomLabel label="Patient Name" data={renderPatientName()} />
                                                            </Grid>

                                                            <Grid className='d-flex justify-content-between'>
                                                                <CustomLabel label="Patient Gender" data={claimAllDetails?.patientGender} />
                                                            </Grid>
                                                            <hr />
                                                            <Grid className='d-flex justify-content-between'>
                                                                <CustomLabel label="Patient City" data={claimAllDetails?.patientCity} />
                                                            </Grid>
                                                            <Grid className='d-flex justify-content-between'>
                                                                <CustomLabel label="Patient State" data={claimAllDetails?.patientState} />
                                                            </Grid>
                                                            <hr />
                                                            <Grid className='d-flex justify-content-between'>
                                                                <CustomLabel label="Zip" data={claimAllDetails?.patientZip} />
                                                            </Grid>
                                                        </Grid>
                                                        <Grid className={classes.tabSecBorder}></Grid>
                                                        <Grid item xs={6} className={classes.tabSecRight}>
                                                            <Grid className='d-flex justify-content-between'>
                                                                <CustomLabel label="Patient MRN" data={claimAllDetails?.patientMrn} />
                                                            </Grid>

                                                            <Grid className='d-flex justify-content-between'>
                                                                <CustomLabel label="Patient DOB" data={asDate(claimAllDetails?.patientDob)} />
                                                            </Grid>
                                                            <Grid className='d-flex justify-content-between'>
                                                                <CustomLabel label="Patient Age" data={claimAllDetails?.age} />
                                                            </Grid>
                                                            <hr />
                                                            <Grid className='d-flex justify-content-between'>
                                                                <CustomLabel label="Physician Name" data={renderPhysicianName()} />
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                </> : tab === 2 ? <>
                                                    <Box sx={{ overflowX: 'auto', width: '100%' }}>
                                                    {claimAllDetails?.remitResponse?.remitGroups?.length > 0 ? (
                                                        <Grid container wrap="nowrap" spacing={4}>
                                                        {claimAllDetails?.remitResponse?.remitGroups?.map((step, index) => (
                                                            <Grid item key={`remit-timeline-${index}`}>
                                                                <Box className={classes.stepContainer}>
                                                                    <Tooltip
                                                                    placement="top"
                                                                    arrow
                                                                    title={
                                                                        <Paper className={classes.tooltipPaper}>
                                                                        <Table size="small">
                                                                            <TableHead>
                                                                            <TableRow>
                                                                                <TableCell className={classes.tableHeadCell}>Date</TableCell>
                                                                                <TableCell className={classes.tableHeadCell}>Amount</TableCell>
                                                                                <TableCell className={classes.tableHeadCell}>Status</TableCell>
                                                                                <TableCell className={classes.tableHeadCell}>Remarks</TableCell>
                                                                                <TableCell className={classes.tableHeadCell}>CAS Adjustments</TableCell>
                                                                            </TableRow>
                                                                            </TableHead>
                                                                            <TableBody>
                                                                            {step.remits?.map((item, i) => (
                                                                                <TableRow key={i}>
                                                                                    <TableCell className={classes.tableCell}>{asDate(item.checkEftDate)}</TableCell>
                                                                                    <TableCell className={classes.tableCell}>{asDollarsWithZero(item.paidAmount)}</TableCell>
                                                                                    <TableCell className={classes.tableCell}>{getStatusText(item.claimStatusCode)}</TableCell>
                                                                                    <TableCell className={classes.tableCell}>{getRemarks(item)}</TableCell>
                                                                                    <TableCell className={classes.tableCell}>{getCasAdjustments(item)}</TableCell>
                                                                                </TableRow>
                                                                            ))}
                                                                            </TableBody>
                                                                        </Table>
                                                                        </Paper>
                                                                    }
                                                                    PopperProps={{
                                                                        sx: {
                                                                            '& .MuiTooltip-tooltip': {
                                                                                backgroundColor: 'transparent',
                                                                                padding: 0,
                                                                            },
                                                                        },
                                                                    }}
                                                                    >
                                                                    <Box className={classes.circle}>{index + 1}</Box>
                                                                    </Tooltip>

                                                                    <Typography className={classes.date}>{asDate(step.checkEftDate)}</Typography>

                                                                    {claimAllDetails?.remitResponse?.remitGroups?.length > 1 && index !== claimAllDetails.remitResponse.remitGroups.length - 1 && (
                                                                        <Box className={classes.line} />
                                                                    )}
                                                                </Box>
                                                            </Grid>
                                                        ))}
                                                        </Grid>
                                                    ) : (
                                                        <Typography variant="body1" sx={{ padding: 2 }}>
                                                            No Remit timelines available.
                                                        </Typography>
                                                    )}
                                                    </Box>
                                                </> :tab === 4 ? <>
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={6} className={classes.tabSecLeft}>
                                                            <Grid className='d-flex justify-content-between'>
                                                                <CustomLabel label="Patient Name" data={renderPatientName()} />
                                                            </Grid>
                                                            <hr />
                                                            <Grid className='d-flex justify-content-between'>
                                                                <CustomLabel label="Patient DOB" data={asDate(claimAllDetails?.patientDob)} />
                                                            </Grid>
                                                            <Grid className='d-flex justify-content-between'>
                                                                    <CustomLabel label="DOS" data={asDate(claimAllDetails?.servicedate) || 'N/A'} />
                                                                </Grid>

                                                            <hr />
                                                            <Grid className='d-flex justify-content-between'>
                                                                <CustomLabel label="Member ID" data={claimAllDetails?.snResponsecontactidentifier} />
                                                            </Grid>
                                                        </Grid>
                                                        <Grid className={classes.tabSecBorder}></Grid>
                                                        <Grid item xs={6} className={classes.tabSecRight}>
                                                            <Grid className='d-flex justify-content-between'>
                                                                <CustomLabel label="Payer Name" data={claimAllDetails?.usePlanIdMapping ? claimAllDetails?.planPayerContract?.payerName:claimAllDetails?.payer?.name} />
                                                            </Grid>
                                                            <hr/>

                                                            <Grid className='d-flex justify-content-between'>
                                                                <CustomLabel label="Claim Amount" data={`$ ${claimAllDetails?.chargedAmount}`} />
                                                            </Grid>
                                                            <Grid className='d-flex justify-content-between'>
                                                                <CustomLabel label="Claim Control No" data={claimAllDetails?.payerClaimControlNumber} />
                                                            </Grid>
                                                            <hr />
                                                            <Grid className='d-flex justify-content-between'>
                                                                <CustomLabel label="NPI" data={claimAllDetails?.billingProviderNpi} />
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                </> :tab === 5 ? <>
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={6} className={`${classes.tabSecLeft} ${classes.eapgGroupsSummary}`}>
                                                            <Grid key={`grouping-summary`} className='d-flex justify-content-between'>
                                                                <CustomLabel label={`Grouping Summary`} data={''} />
                                                            </Grid>
                                                            {claimAllDetails?.eapgClaimExecutionGroups?.length > 0 && claimAllDetails.eapgClaimExecutionGroups.map((item, index) => (
                                                                <>
                                                                <Grid key={`${item?.name}-${index}`} className='d-flex justify-content-between'>
                                                                    <CustomLabel label={`${item?.name}`} 
                                                                            data={<PopoverLink
                                                                                columnData={item?.code}
                                                                                hoverData={[
                                                                                    {
                                                                                        label: "",
                                                                                        data: item?.description
                                                                                    }
                                                                                ]}
                                                                                title={item?.code}
                                                                            />} />
                                                                </Grid>
                                                                <Grid key={`eapg-weight-${index}`} className='d-flex justify-content-between'>
                                                                    <CustomLabel label={`EAPG Weight`} data={`${item?.eapgWeight}`} />
                                                                </Grid>
                                                                <br />
                                                                </>
                                                            ))}
                                                        </Grid>
                                                        <Grid className={classes.tabSecBorder}></Grid>
                                                        <Grid item xs={6} className={classes.tabSecRight}>
                                                            <Grid key={`reimbursement-summary`} className='d-flex justify-content-between'>
                                                                <CustomLabel label={`Reimbursement Summary`} data={''} />
                                                            </Grid>
                                                            {claimAllDetails?.eapgClaimExecutionSchedules?.length > 0 &&
                                                                <Grid key={`reimbursementAmount`} className='d-flex justify-content-between'>
                                                                    <CustomLabel label={`Reimbursement Amount`} data={claimAllDetails?.eapgClaimExecutionSchedules[0]?.reimbursementAmount || '0.00'} />
                                                                </Grid>
                                                            }
                                                            <hr />
                                                            <Grid className={classes.eapgIssuesSummary}>
                                                                <Grid key={`issues-reported`} className='d-flex justify-content-between'>
                                                                    <CustomLabel label={`Issues Reported`} data={''} />
                                                                </Grid>
                                                                {claimAllDetails?.eapgClaimExecutionIssues?.length > 0 && claimAllDetails.eapgClaimExecutionIssues.map((item, index) => (
                                                                    <Grid key={index} className='d-flex justify-content-between'>
                                                                        <Tooltip title={item?.description || ''} placement="top" arrow>
                                                                            <div style={{ width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                            <InfoIcon className="mr-2" color="error" />
                                                                            <CustomLabel
                                                                                label={item?.name}
                                                                                data={item?.description}
                                                                            />
                                                                            </div>
                                                                        </Tooltip>
                                                                    </Grid>
                                                                ))}
                                                            </Grid>
                                                            
                                                        </Grid>
                                                    </Grid>
                                                </> :tab === 6 ? <>
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={6} className={classes.tabSecLeft}>
                                                            <Grid key={`schedules-summary`} className='d-flex justify-content-between'>
                                                                <CustomLabel label={`Schedules Summary`} data={''} />
                                                            </Grid>
                                                            {claimAllDetails?.eapgClaimExecutionSchedules?.length > 0 && claimAllDetails?.eapgClaimExecutionSchedules.map((item, index) => (
                                                                <>
                                                                <Grid key={`begin-date-${index}`} className='d-flex justify-content-between'>
                                                                    <CustomLabel label={`Begin Date`} data={asDate(item?.beginDate) || 'N/A'} />
                                                                </Grid>
                                                                <Grid key={`end-date-${index}`} className='d-flex justify-content-between'>
                                                                    <CustomLabel label={`End Date`} data={asDate(item?.endDate) || 'N/A'} />
                                                                </Grid>
                                                                <Grid key={`schedule-description-${index}`} className='d-flex justify-content-between'>
                                                                    <CustomLabel label={`Description`} data={item?.description || 'N/A'} />
                                                                </Grid>
                                                                <Grid key={`grouper-type-${index}`} className='d-flex justify-content-between'>
                                                                    <CustomLabel label={`Grouper Type`} data={item?.grouperTypeUsed || 'N/A'} />
                                                                </Grid>
                                                                <Grid key={`grouper-version-${index}`} className='d-flex justify-content-between'>
                                                                    <CustomLabel label={`Grouper Version`} data={item?.grouperVersionUsed || 'N/A'} />
                                                                </Grid>
                                                                <Grid key={`reimbursement-type-${index}`} className='d-flex justify-content-between'>
                                                                    <CustomLabel label={`Reimbursement Type`} data={item?.reimbursementType || 'N/A'} />
                                                                </Grid>
                                                                </>
                                                            ))}
                                                        </Grid>
                                                        <Grid className={classes.tabSecBorder}></Grid>
                                                        <Grid item xs={6} className={classes.tabSecRight}>
                                                            
                                                        </Grid>
                                                    </Grid>
                                                </>:
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={6} className={classes.tabSecLeft}>
                                                            <Grid className='d-flex justify-content-between'>
                                                                <CustomLabel label="Claim Analysis Version" data={claimAllDetails?.version} />
                                                            </Grid>
                                                        </Grid>
                                                        <Grid className={classes.tabSecBorder}></Grid>
                                                        <Grid item xs={6} className={classes.tabSecRight}>
                                                            <Grid className='d-flex justify-content-between'>
                                                                <CustomLabel label="Contract Version" data={claimAllDetails?.contract?.version} />
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>

                                                }
                                            </Grid>

                                        </Box>
                                    </Collapse>
                                </BCard.Body>
                            </BCard>
                        </Grid>

                        <Grid sm={5} md={5} lg={5} xl={4} item>
                            <BCard className={classes.sectionCard}>
                                <BCard.Header as="h5" className={classes.sectionHeader}>
                                    Claim Stats
                                </BCard.Header>
                                <BCard.Body className='p-0'>
                                    <Collapse in={true} timeout="auto" unmountOnExit>
                                        <Box className={classes.box} marginTop={1} marginBottom={1}>
                                            <Grid container className='justify-content-between'>
                                                <Grid item sm={6} className='pt-0'>
                                                    <SummaryTile isVertical={true} title="Claim Total" helpText="Total charges associated with current claim" value={claimAllDetails?.chargedAmount} color="warning" cardPadding='2px 10px' />
                                                </Grid>
                                                {!isFromModel && <Grid item sm={6} className='pt-0'>
                                                    <SummaryTile isVertical={true} title="Payment" helpText="Amount paid (cash) by the current payer according to the ERA" value={claimAllDetails?.claimPaidamount} color="success" cardPadding='2px 10px' />
                                                </Grid>}
                                            </Grid>
                                            <Grid container className='justify-content-between'>
                                                {isFromModel && claimAllDetails?.totalExpectedPayNames?.length > 0 && claimAllDetails?.totalExpectedPayValues?.length > 0 &&
                                                    <>
                                                        <Grid item sm={12} className='pt-0'>
                                                            <b className={`kt-font-success ${classes.totalExpectedPayLabel}`}>{`Total Expected Pay:`}</b>
                                                        </Grid>

                                                        {claimAllDetails?.totalExpectedPayNames?.map((pay, index) => (
                                                            <Grid item sm={6} className='pt-0'>
                                                                <SummaryTile isVertical={true} title={pay.split('-')[0] || ''} helpText="Total amount expected to be paid by the current payer (including Patient Resp.) Calculated by SlicedHealth" value={claimAllDetails?.totalExpectedPayValues[index] || ''} color="info" cardPadding='2px 10px' />
                                                            </Grid>
                                                        ))}
                                                    </>
                                                }
                                                {!isFromModel && <Grid item sm={6} className='pt-0'>
                                                    <SummaryTile isVertical={true} title="Total Expected Pay" helpText="Total amount expected to be paid by the current payer (including Patient Resp.) Calculated by SlicedHealth" value={claimAllDetails?.expectedPay} color="info" cardPadding='2px 10px' />
                                                </Grid>}
                                                {!isFromModel && <Grid item sm={6} className='pt-0'>
                                                    <SummaryTile isVertical={true} title="Patient Responsibility" helpText="Amount owed by the patient according to the ERA" value={claimAllDetails?.patientResponsibility} color="success" cardPadding='2px 0px 2px 10px' />
                                                </Grid>}

                                            </Grid>
                                            {!isFromModel && <Grid container className='justify-content-between'>
                                                <Grid item sm={6} className='pb-0'>
                                                    <SummaryTile isVertical={true} title="Total Variance" helpText="Difference in Expected Pay and Total allowable.  Calculated by SlicedHealth" value={claimAllDetails?.variance} color="danger" cardPadding='2px 10px' />
                                                </Grid>
                                                <Grid item sm={6} className='pt-0'>
                                                    <SummaryTile isVertical={true} title="Other Allowable" helpText="Amount adjusted by the payer that affects the allowable according to the ERA (CAS codes set in configuration screen)" value={claimAllDetails?.otherAllowable} color="success" cardPadding='2px 10px' />
                                                </Grid>
                                            </Grid>}
                                            {!isFromModel && <Grid container className='justify-content-between'>
                                                <Grid item sm={6} className='pb-0'>
                                                    {(recoveredAmountFlag === true) && (
                                                        <SummaryTile isVertical={true} title="Recovered Amount" helpText="Differences in variance between previous ERA and most recent ERA" value={claimAllDetails?.recoveredAmount} color="danger" cardPadding='2px 10px' />
                                                    )}
                                                </Grid>
                                                <Grid item sm={6} className='pt-0'>
                                                    <SummaryTile isVertical={true} title="Total Allowable" helpText="Total reimbursement from current payer according to the ERA. (Payment + Patient Resp. + Other Allowable)" value={claimAllDetails?.actualPay} color="success" cardPadding='2px 10px' />
                                                </Grid>

                                            </Grid>}
                                        </Box>
                                    </Collapse>
                                </BCard.Body>
                            </BCard>
                        </Grid>
                    </Grid >
                </Grid >
            </Loadable>
            <AdvancedTrace sections={sections} isTraceOpen={isTraceOpen} setTraceOpen={setTraceOpen} title={claimAllDetails?.claimnumber} chargeLevelSections={chargeLevelSections} />
            <ClaimHistory
                open={isHistoryOpen}
                handleClose={handleCloseHistory}
                claimId={claimAllDetails?.id}
            />
            <ArchivedClaimDetails />
            <Dialog
                fullWidth
                scroll="paper"
                maxWidth="xs"
                open={isProcessClaimResponseDialogOpen}
                onClose={handleProcessClaimDialogClose}
                aria-labelledby="form-dialog-title">
                <DialogTitle>
                    <Grid container alignItems='center'>
                        <Grid item xs={11}>
                            Claim Processing ({processClaimResponse?.claimNumber})
                        </Grid>
                        <Grid item xs={1} className='text-right'>
                            <IconButton aria-label="close" onClick={handleProcessClaimDialogClose} size="large">
                                <CloseIcon />
                            </IconButton>
                        </Grid>
                    </Grid>
                </DialogTitle>
                <DialogContent dividers>
                    <Grid>
                        <CustomLabel label='Claim Number' data={processClaimResponse?.claimNumber} />
                    </Grid>
                    <Grid>
                        <CustomLabel label='State' data={<b className='kt-font-danger'>{processClaimResponse?.processingState}</b>} />
                    </Grid>
                    <Grid>
                        <CustomLabel label='Message' data={processClaimResponse?.error} />
                    </Grid>
                </DialogContent>
            </Dialog>
            <ConfirmationDialog handleNextClaim={handleNextClaim} handlePreviousClaim={handlePreviousClaim} handleClose={handleDialogClose} />
            <PatientDetail claimId={claimAllDetails?.id} isOpen={isPatientDetailsOpen} handleClose={()=>setPatientDetailsOpen(false)} isFhirConfigured={isFHIRConfigured} />
        </Grid>)
    );
}

export default withRouter(BasicDetailsSection);